import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getFileUrl } from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/layout/Header';
import { Button } from '../../components/common/Common';
import {
  FolderKanban, Plus, Search, Trash2, Edit2, Eye,
  CheckCircle2, AlertCircle, Building2, MapPin, Image as ImageIcon,
  LayoutGrid, List as ListIcon, X
} from 'lucide-react';
import './AllProjects.css';

const TYPE_COLORS = {
  residential: { color: '#2563eb', bg: '#eff6ff', label: 'Residential' },
  commercial:  { color: '#7c3aed', bg: '#f5f3ff', label: 'Commercial' },
  mixed:       { color: '#b45309', bg: '#fffbeb', label: 'Mixed' },
};

const STATUS_COLORS = {
  'pre-launch':         { color: '#b45309', bg: '#fef3c7', label: 'Pre-Launch' },
  'under-construction': { color: '#d97706', bg: '#fef3c7', label: 'Under Construction' },
  'ready-to-move':      { color: '#059669', bg: '#ecfdf5', label: 'Ready to Move' },
  'completed':          { color: '#059669', bg: '#ecfdf5', label: 'Completed' },
};

export default function AllProjects() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission, isAdmin } = useAuth();

  const canAdd = isAdmin || hasPermission('projects.add');
  const canEdit = isAdmin || hasPermission('projects.edit');
  const canDelete = isAdmin || hasPermission('projects.delete');

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [view, setView] = useState('grid');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('projects?limit=200');
      if (res.success) {
        const list = res.data?.projects || (Array.isArray(res.data) ? res.data : []);
        setProjects(list);
      }
    } catch (err) {
      console.error('Fetch projects error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const filtered = projects.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (p.name || '').toLowerCase().includes(q) ||
      (p.developer || '').toLowerCase().includes(q) ||
      (p.rera_number || '').toLowerCase().includes(q) ||
      (p.city || p.location || '').toLowerCase().includes(q);
    const matchType = !typeFilter || (p.project_type || '').toLowerCase() === typeFilter;
    const matchStatus = !statusFilter || (p.construction_status || '').toLowerCase() === statusFilter;
    return matchSearch && matchType && matchStatus;
  });

  const handleDelete = async (id) => {
    try {
      const res = await apiFetch(`projects/${id}`, { method: 'DELETE' });
      if (res.success) { showToast('Project deleted'); fetchProjects(); }
      else showToast(res.message || 'Failed', 'error');
    } catch (err) { showToast('Failed to delete', 'error'); }
    setDeleteConfirm(null);
  };

  const activeCount = projects.filter(p => p.is_active !== 0 && p.is_active !== false).length;
  const totalUnits = projects.reduce((s, p) => s + (Number(p.planned_units) || 0), 0);
  const totalTowers = projects.reduce((s, p) => s + (Number(p.tower_count) || 0), 0);

  return (
    <div>
      <Header
        title="All Projects"
        subtitle={`${projects.length} project${projects.length !== 1 ? 's' : ''} registered`}
        actions={canAdd ? (
          <Button variant="gold" icon={Plus} onClick={() => navigate('/projects/add')}>
            New Project
          </Button>
        ) : null}
      />
      <div className="ajp-page page">

      {/* Stats strip */}
      <div className="ajp-stat-strip">
        {[
          {
            label: 'Total Projects',
            value: projects.length,
            icon: FolderKanban,
            color: '#1a2035',
            bg: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
            sub: projects.length === 0 ? 'No projects yet' : `${projects.length === 1 ? '1 project' : `${projects.length} projects`} registered`,
          },
          {
            label: 'Active',
            value: activeCount,
            icon: CheckCircle2,
            color: '#16a34a',
            bg: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
            sub: projects.length > 0 ? `${Math.round((activeCount / projects.length) * 100)}% active` : '—',
          },
          {
            label: 'Total Towers',
            value: totalTowers,
            icon: Building2,
            color: '#2563eb',
            bg: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
            sub: projects.length > 0 ? `~${(totalTowers / projects.length).toFixed(1)} per project` : '—',
          },
          {
            label: 'Total Units',
            value: totalUnits.toLocaleString('en-IN'),
            icon: AlertCircle,
            color: '#d97706',
            bg: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            sub: totalTowers > 0 ? `~${Math.round(totalUnits / totalTowers)} per tower` : '—',
          },
        ].map((s, i) => (
          <div className="ajp-stat-cell" key={s.label}>
            <div className="ajp-stat-cell__icon" style={{ color: s.color, background: s.bg }}>
              <s.icon size={18} />
            </div>
            <div className="ajp-stat-cell__body">
              <span className="ajp-stat-cell__val">{s.value}</span>
              <span className="ajp-stat-cell__label">{s.label}</span>
              <span className="ajp-stat-cell__sub">{s.sub}</span>
            </div>
            {i < 3 && <span className="ajp-stat-cell__divider" />}
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="ajp-toolbar ajp-toolbar--pill">
        <div className="ajp-search ajp-search--pill">
          <Search size={15} />
          <input placeholder="Search by name, developer, RERA, city…" value={search} onChange={e => setSearch(e.target.value)} />
          {search && (
            <button className="ajp-search__clear" onClick={() => setSearch('')} title="Clear">
              <X size={13} />
            </button>
          )}
        </div>
        <div className="ajp-toolbar__right">
          <div className="ajp-pill-select">
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="">All Types</option>
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
              <option value="mixed">Mixed Use</option>
            </select>
          </div>
          <div className="ajp-pill-select">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="pre-launch">Pre-Launch</option>
              <option value="under-construction">Under Construction</option>
              <option value="ready-to-move">Ready to Move</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          {(typeFilter || statusFilter || search) && (
            <button className="ajp-toolbar__reset" onClick={() => { setSearch(''); setTypeFilter(''); setStatusFilter(''); }} title="Clear filters">
              Reset
            </button>
          )}
          <div className="ajp-view-toggle">
            <button className={`ajp-view-btn ${view === 'grid' ? 'ajp-view-btn--active' : ''}`} onClick={() => setView('grid')} title="Grid view">
              <LayoutGrid size={15} />
            </button>
            <button className={`ajp-view-btn ${view === 'list' ? 'ajp-view-btn--active' : ''}`} onClick={() => setView('list')} title="List view">
              <ListIcon size={15} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="ajp-empty"><p>Loading...</p></div>
      ) : filtered.length === 0 ? (
        <div className="ajp-empty">
          <FolderKanban size={36} style={{ opacity: 0.2, marginBottom: 8 }} />
          <p>No projects found</p>
        </div>
      ) : view === 'grid' ? (
        <div className="ajp-grid">
          {filtered.map(p => {
            const typeKey = (p.project_type || '').toLowerCase();
            const statusKey = (p.construction_status || '').toLowerCase();
            const typeStyle = TYPE_COLORS[typeKey] || { color: '#64748b', bg: '#f1f5f9', label: p.project_type };
            const statusStyle = STATUS_COLORS[statusKey] || { color: '#64748b', bg: '#f1f5f9', label: p.construction_status };
            const cover = p.cover_image ? getFileUrl(p.cover_image) : null;
            const cityLine = [p.city, p.state].filter(Boolean).join(', ') || p.location || '—';

            return (
              <div className="ajp-card" key={p.project_id} onClick={() => navigate(`/projects/view/${p.project_id}`)}>
                <div className="ajp-card__cover">
                  {cover ? (
                    <img src={cover} alt={p.name} />
                  ) : (
                    <div className="ajp-card__cover-placeholder">
                      <ImageIcon size={32} />
                    </div>
                  )}
                  {p.construction_status && (
                    <span className="ajp-card__status-badge" style={{ color: statusStyle.color, background: statusStyle.bg }}>
                      {statusStyle.label}
                    </span>
                  )}
                </div>
                <div className="ajp-card__body">
                  <div className="ajp-card__title-row">
                    <h3 className="ajp-card__title" title={p.name}>{p.name}</h3>
                    {p.project_type && (
                      <span className="ajp-badge" style={{ color: typeStyle.color, background: typeStyle.bg }}>
                        {typeStyle.label}
                      </span>
                    )}
                  </div>
                  <p className="ajp-card__dev">{p.developer || '—'}</p>
                  <p className="ajp-card__location"><MapPin size={12} /> {cityLine}</p>

                  <div className="ajp-card__meta">
                    <div className="ajp-card__meta-item">
                      <span className="ajp-card__meta-val">{p.tower_count ?? 0}</span>
                      <span className="ajp-card__meta-label">Towers</span>
                    </div>
                    <div className="ajp-card__meta-item">
                      <span className="ajp-card__meta-val">{p.planned_units ?? 0}</span>
                      <span className="ajp-card__meta-label">Units</span>
                    </div>
                    <div className="ajp-card__meta-item">
                      <span className="ajp-card__meta-val">{p.media_count ?? 0}</span>
                      <span className="ajp-card__meta-label">Media</span>
                    </div>
                  </div>

                  {p.rera_number && (
                    <p className="ajp-card__rera">RERA: {p.rera_number}</p>
                  )}

                  <div className="ajp-card__actions" onClick={e => e.stopPropagation()}>
                    <button className="ajp-icon-btn" title="View" onClick={() => navigate(`/projects/view/${p.project_id}`)}>
                      <Eye size={14} />
                    </button>
                    {canEdit && (
                      <button className="ajp-icon-btn" title="Edit" onClick={() => navigate(`/projects/edit/${p.project_id}`)}>
                        <Edit2 size={14} />
                      </button>
                    )}
                    {canDelete && (
                      <button className="ajp-icon-btn ajp-icon-btn--danger" title="Delete" onClick={() => setDeleteConfirm(p.project_id)}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="ajp-table-wrap">
          <table className="ajp-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Project Name</th>
                <th>Developer</th>
                <th>RERA</th>
                <th>Type</th>
                <th>Status</th>
                <th>Location</th>
                <th>Towers</th>
                <th>Units</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const typeKey = (p.project_type || '').toLowerCase();
                const statusKey = (p.construction_status || '').toLowerCase();
                const typeStyle = TYPE_COLORS[typeKey] || { color: '#64748b', bg: '#f1f5f9', label: p.project_type };
                const statusStyle = STATUS_COLORS[statusKey] || { color: '#64748b', bg: '#f1f5f9', label: p.construction_status };
                return (
                  <tr key={p.project_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/view/${p.project_id}`)}>
                    <td className="ajp-td--muted">{i + 1}</td>
                    <td>
                      <div className="ajp-project-name">
                        <div className="ajp-project-icon"><Building2 size={15} /></div>
                        <span className="ajp-td--bold">{p.name}</span>
                      </div>
                    </td>
                    <td className="ajp-td--muted">{p.developer || '-'}</td>
                    <td className="ajp-td--muted" style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.rera_number || '-'}</td>
                    <td>{p.project_type ? <span className="ajp-badge" style={{ color: typeStyle.color, background: typeStyle.bg }}>{typeStyle.label}</span> : '-'}</td>
                    <td>{p.construction_status ? <span className="ajp-badge" style={{ color: statusStyle.color, background: statusStyle.bg }}>{statusStyle.label}</span> : '-'}</td>
                    <td className="ajp-td--muted">{[p.city, p.state].filter(Boolean).join(', ') || p.location || '-'}</td>
                    <td className="ajp-td--muted">{p.tower_count ?? '-'}</td>
                    <td className="ajp-td--muted">{p.planned_units ?? '-'}</td>
                    <td className="ajp-td--actions" onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="ajp-icon-btn" title="View" onClick={() => navigate(`/projects/view/${p.project_id}`)}><Eye size={14} /></button>
                        {canEdit && <button className="ajp-icon-btn" title="Edit" onClick={() => navigate(`/projects/edit/${p.project_id}`)}><Edit2 size={14} /></button>}
                        {canDelete && <button className="ajp-icon-btn ajp-icon-btn--danger" title="Delete" onClick={() => setDeleteConfirm(p.project_id)}><Trash2 size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="ajp-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="ajp-confirm" onClick={e => e.stopPropagation()}>
            <div className="ajp-confirm__icon"><Trash2 size={28} /></div>
            <h3>Delete Project?</h3>
            <p>This action cannot be undone.</p>
            <div className="ajp-confirm__btns">
              <button className="ajp-btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="ajp-btn-danger" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
